import { Theme } from '@mui/material';
import { includes } from 'lodash';

import { COLORS } from '@/styles/tokens/colors';
import { FONT_SIZING } from '@/styles/tokens/fonts';
import { UnreachableError } from '@/utils/errors';

import { ButtonSize, ButtonVariant } from './types';

interface RegularButtonColorDefinition {
  backgroundColor: string;
  backgroundColorOnHover: string;
  textColor: string;
  borderColor: string;
  textColorOnHover?: string;
}

interface DisabledButtonColorDefinition {
  backgroundColor?: string;
  backgroundOnHover?: string;
  textColor?: string;
  borderColor: string;
}

interface ButtonColorMap {
  regular: RegularButtonColorDefinition;
  disabled: DisabledButtonColorDefinition;
}
type ColorsByVariant = Record<ButtonVariant, ButtonColorMap>;
export function getColorsForVariant(
  variant: ButtonVariant,
  theme: Theme,
  // In presentations (view-only) keep the tenant's branded button color for
  // secondary labels; elsewhere use the primary text color.
  isViewOnly = false
) {
  const buttonColor = theme.palette.buttons;
  const isDarkMode = theme.palette.mode === 'dark';

  const secondary: ButtonColorMap = {
    regular: {
      backgroundColor: theme.palette.common.white,
      backgroundColorOnHover: COLORS.GRAY[100],
      textColor: isViewOnly ? buttonColor.main : COLORS.TEXT.PRIMARY,
      borderColor: COLORS.GRAY[300],
    },
    disabled: {
      borderColor: COLORS.GRAY[100],
      backgroundColor: theme.palette.common.white,
      textColor: COLORS.GRAY[300],
    },
  };

  const transparent: ButtonColorMap = isDarkMode
    ? {
        regular: {
          backgroundColor: 'transparent',
          backgroundColorOnHover: COLORS.NAVY[800],
          textColor: COLORS.NAVY[400],
          textColorOnHover: COLORS.NAVY[400],
          borderColor: 'transparent',
        },
        disabled: {
          textColor: COLORS.NAVY[800],
          borderColor: 'transparent',
        },
      }
    : {
        regular: {
          backgroundColor: 'transparent',
          backgroundColorOnHover: COLORS.FUNCTIONAL.HOVER,
          textColor: COLORS.TEXT.PRIMARY,
          textColorOnHover: COLORS.TEXT.PRIMARY,
          borderColor: 'transparent',
        },
        disabled: {
          textColor: COLORS.GRAY[300],
          borderColor: 'transparent',
        },
      };

  const colorsByVariant: ColorsByVariant = {
    primary: {
      regular: {
        backgroundColor: buttonColor.main,
        borderColor: buttonColor.main,
        textColor: buttonColor.contrastText,
        backgroundColorOnHover: buttonColor.dark,
      },
      disabled: {
        backgroundColor: buttonColor.light,
        borderColor: buttonColor.light,
      },
    },

    secondary,
    'destructive-secondary': {
      regular: {
        backgroundColor: theme.palette.common.white,
        backgroundColorOnHover: COLORS.FUNCTIONAL.ERROR[50],
        textColor: COLORS.FUNCTIONAL.ERROR[700],
        borderColor: COLORS.FUNCTIONAL.ERROR[200],
      },
      disabled: {
        borderColor: COLORS.FUNCTIONAL.ERROR[50],
        backgroundColor: COLORS.FUNCTIONAL.ERROR[50],
        textColor: COLORS.FUNCTIONAL.ERROR[300],
      },
    },
    'destructive-transparent': {
      regular: {
        backgroundColor: 'transparent',
        backgroundColorOnHover: COLORS.FUNCTIONAL.ERROR[50],
        textColor: COLORS.FUNCTIONAL.ERROR[700],
        textColorOnHover: COLORS.FUNCTIONAL.ERROR[700],
        borderColor: 'transparent',
      },
      disabled: {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        textColor: COLORS.FUNCTIONAL.ERROR[300],
      },
    },
    'destructive-primary': {
      regular: {
        backgroundColor: COLORS.FUNCTIONAL.ERROR.DEFAULT,
        backgroundColorOnHover: COLORS.FUNCTIONAL.ERROR[700],
        textColor: theme.palette.common.white,
        borderColor: COLORS.FUNCTIONAL.ERROR.DEFAULT,
      },
      disabled: {
        borderColor: COLORS.FUNCTIONAL.ERROR[300],
        backgroundColor: COLORS.FUNCTIONAL.ERROR[300],
        textColor: theme.palette.common.white,
      },
    },
    'success-primary': {
      regular: {
        backgroundColor: COLORS.TEAL[500],
        borderColor: COLORS.TEAL[500],
        textColor: theme.palette.common.white,
        backgroundColorOnHover: COLORS.TEAL[600],
      },
      disabled: {
        backgroundColor: COLORS.TEAL[300],
        borderColor: COLORS.TEAL[300],
        textColor: theme.palette.common.white,
      },
    },
    'success-secondary': {
      regular: {
        backgroundColor: theme.palette.common.white,
        backgroundColorOnHover: COLORS.TEAL[50],
        textColor: COLORS.TEAL[700],
        borderColor: COLORS.TEAL[200],
      },
      disabled: {
        backgroundColor: COLORS.TEAL[50],
        borderColor: COLORS.TEAL[50],
        textColor: COLORS.TEAL[300],
      },
    },
    'success-transparent': {
      regular: {
        backgroundColor: 'transparent',
        backgroundColorOnHover: COLORS.TEAL[50],
        textColor: COLORS.TEAL[700],
        textColorOnHover: COLORS.TEAL[700],
        borderColor: 'transparent',
      },
      disabled: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        textColor: COLORS.TEAL[300],
      },
    },
    transparent,
    'transparent-inverse': {
      regular: {
        backgroundColor: 'transparent',
        backgroundColorOnHover: COLORS.GRAY[50],
        textColor: 'white',
        textColorOnHover: COLORS.GRAY[600],
        borderColor: 'transparent',
      },
      disabled: {
        textColor: COLORS.GRAY[300],
        borderColor: 'transparent',
      },
    },
    prominent: {
      regular: {
        backgroundColor: COLORS.BRAND.ORANGE,
        borderColor: COLORS.BRAND.ORANGE,
        textColor: COLORS.PRIMITIVES.WHITE,
        backgroundColorOnHover: COLORS.ORANGE[600],
      },
      disabled: {
        backgroundColor: COLORS.ORANGE[200],
        borderColor: COLORS.ORANGE[200],
        textColor: COLORS.PRIMITIVES.WHITE,
      },
    },
    'active-group-element': {
      regular: {
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        borderColor: COLORS.PRIMITIVES.WHITE,
        textColor: COLORS.TEXT.PRIMARY,
        backgroundColorOnHover: COLORS.PRIMITIVES.WHITE,
      },
      disabled: {
        backgroundColor: COLORS.GRAY[200],
        borderColor: COLORS.GRAY[200],
        textColor: COLORS.GRAY[400],
      },
    },
    'inactive-group-element': {
      regular: {
        backgroundColor: COLORS.GRAY[100],
        borderColor: COLORS.GRAY[100],
        textColor: COLORS.GRAY[500],
        backgroundColorOnHover: COLORS.GRAY[200],
      },
      disabled: {
        backgroundColor: COLORS.GRAY[100],
        borderColor: COLORS.GRAY[100],
        textColor: COLORS.GRAY[300],
      },
    },
    'inactive-group-element-dark': {
      regular: {
        backgroundColor: COLORS.GRAY[300],
        borderColor: COLORS.GRAY[300],
        textColor: COLORS.GRAY[500],
        backgroundColorOnHover: COLORS.GRAY[200],
      },
      disabled: {
        backgroundColor: COLORS.GRAY[300],
        borderColor: COLORS.GRAY[300],
        textColor: COLORS.GRAY[400],
      },
    },
  };

  return colorsByVariant[variant];
}

export const getCSSPropertiesByVariant = (
  variant: ButtonVariant,
  disabled: boolean,
  theme: Theme,
  isViewOnly = false
) => {
  const colors = getColorsForVariant(variant, theme, isViewOnly);
  const secondary = {
    backgroundColor: colors.regular.backgroundColor,
    color: colors.regular.textColor,
    border: `solid ${colors.regular.borderColor} 1px`,
    ':hover': {
      backgroundColor: colors.regular.backgroundColorOnHover,
    },
    ...(disabled
      ? {
          border: `solid ${colors.disabled.borderColor} 1px`,
          backgroundColor: colors.disabled.backgroundColor,
          color: colors.disabled.textColor,
        }
      : {}),
  };

  switch (variant) {
    case 'primary':
    // Prominent shares primary's solid fill + matching-border hover; only its
    // colors (the Luminary AI orange) differ, via getColorsForVariant.
    case 'prominent': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        color: colors.regular.textColor,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              backgroundColor: colors.disabled.backgroundColor,
              borderColor: colors.disabled.borderColor,
            }
          : {}),
      };
    }
    case 'secondary':
      return secondary;
    case 'destructive-secondary': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              border: `solid ${colors.disabled.borderColor} 1px`,
              backgroundColor: colors.disabled.backgroundColor,
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'destructive-primary': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              border: `solid ${colors.disabled.borderColor} 1px`,
              backgroundColor: colors.disabled.backgroundColor,
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'destructive-transparent': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
          color: colors.regular.textColorOnHover,
        },
        ...(disabled
          ? {
              border: `solid ${colors.disabled.borderColor} 1px`,
              backgroundColor: colors.disabled.backgroundColor,
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'transparent': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
          color: colors.regular.textColorOnHover,
        },
        ...(disabled
          ? {
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'transparent-inverse': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
          color: colors.regular.textColorOnHover,
        },
        ...(disabled
          ? {
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'success-primary': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        color: colors.regular.textColor,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              backgroundColor: colors.disabled.backgroundColor,
              borderColor: colors.disabled.borderColor,
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'success-secondary': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              border: `solid ${colors.disabled.borderColor} 1px`,
              backgroundColor: colors.disabled.backgroundColor,
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'success-transparent': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        color: colors.regular.textColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
          color: colors.regular.textColorOnHover,
        },
        ...(disabled
          ? {
              color: colors.disabled.textColor,
            }
          : {}),
      };
    }
    case 'active-group-element': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        color: colors.regular.textColor,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          borderColor: colors.regular.backgroundColorOnHover,
        },
        ...(disabled
          ? {
              backgroundColor: colors.disabled.backgroundColor,
              borderColor: colors.disabled.borderColor,
            }
          : {}),
      };
    }
    case 'inactive-group-element': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        color: colors.regular.textColor,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          // match the border with the background of the container on hover so there's contrast
          borderColor: colors.regular.backgroundColor,
        },
        ...(disabled
          ? {
              backgroundColor: colors.disabled.backgroundColor,
              borderColor: colors.disabled.borderColor,
            }
          : {}),
      };
    }

    case 'inactive-group-element-dark': {
      return {
        backgroundColor: colors.regular.backgroundColor,
        border: `solid ${colors.regular.borderColor} 1px`,
        color: colors.regular.textColor,
        ':hover': {
          backgroundColor: colors.regular.backgroundColorOnHover,
          // match the border with the background of the container on hover so there's contrast
          borderColor: colors.regular.backgroundColor,
        },
        ...(disabled
          ? {
              backgroundColor: colors.disabled.backgroundColor,
              borderColor: colors.disabled.borderColor,
            }
          : {}),
      };
    }
    default:
      throw new UnreachableError({
        case: variant,
        message: `Unrecognized button variant ${variant}`,
      });
  }
};

interface PaddingDefinition {
  paddingX: number;
  paddingY: number;
}

// an odd number, but we want the height of
// this button to be ~40px and match the height
// of a generic text input
const SMALL_VERTICAL_PADDING_PX = 9;

const paddingBySize: Record<ButtonSize, PaddingDefinition> = {
  // these ButtonGroup-specific sizes seems weird because we need smaller horizontal padding to
  // allow large numbers of buttons to reduce neatly on smaller-width screens.
  _bg: {
    paddingX: 8,
    paddingY: 6,
  },

  xs: {
    paddingX: 8,
    paddingY: 5,
  },
  sm: {
    paddingX: 12,
    paddingY: SMALL_VERTICAL_PADDING_PX,
  },
  md: {
    paddingX: 16,
    paddingY: 12,
  },
};

export function getCSSPropsForButtonPadding(
  size: ButtonSize,
  isSquare: boolean
): React.CSSProperties {
  const paddingDefinition = paddingBySize[size];
  if (isSquare) {
    return {
      padding: paddingDefinition.paddingY,
    };
  }

  return {
    padding: `${paddingDefinition.paddingY}px ${paddingDefinition.paddingX}px`,
  };
}

const fontStyleOverridesBySize: Record<ButtonSize, React.CSSProperties> = {
  _bg: {},
  xs: {},
  sm: {},
  md: {
    fontSize: FONT_SIZING.sm.fontSize,
  },
};

export const iconSizeByButtonSize: Record<ButtonSize, number> = {
  xs: 16,
  _bg: 16,
  sm: 16,
  md: 16,
};

export const spinnerSizeByButtonSize: Record<ButtonSize, number> = {
  xs: 16,
  _bg: 16,
  sm: 20,
  md: 28,
};

export const iconGapByButtonSize: Record<ButtonSize, number> = {
  _bg: 8,
  xs: 4,
  sm: 8,
  md: 8,
};

export function getButtonStyles({
  theme,
  variant,
  size,
  fullWidth,
  disabled,
  whiteSpace,
  square = false,
  isViewOnly = false,
}: {
  theme: Theme;
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth?: boolean;
  square?: boolean;
  disabled?: boolean;
  whiteSpace: React.CSSProperties['whiteSpace'];
  isViewOnly?: boolean;
}) {
  const variantStyle = getCSSPropertiesByVariant(
    variant,
    !!disabled,
    theme,
    isViewOnly
  );
  const fullWidthStyle = fullWidth ? { width: '100%' } : {};
  const fontStyleOverrides = fontStyleOverridesBySize[size];
  const padding = getCSSPropsForButtonPadding(size, square);
  const whiteSpaceStyle = whiteSpace ? { whiteSpace } : {};
  const typographyStyle = theme.typography['body-xs'];

  const baseStyling = {
    borderRadius: theme.shape.borderRadius,
    // note that there are size-specific typography overrides in `styleBySize`
    // so typography must be applied before it
    ...typographyStyle,
    ...variantStyle,
    ...fontStyleOverrides,
    ...padding,
    ...fullWidthStyle,
    ...whiteSpaceStyle,
    ':focus-visible': {
      // for most of the buttons, we can just use the a normal 2px outline.
      // for button group buttons, though, we currently have overflow set to hidden
      // as an implementation detail, so we actually want the outline to be *inset*
      ...(includes(['_bg'], size)
        ? {
            outline: 'solid 2px',
            outlineOffset: -3,
          }
        : {
            outline: 'solid black 2px',
            outlineOffset: 2,
          }),
    },
  };

  return baseStyling;
}
